import slug from 'slug';
import copy from 'copy-to-clipboard';
import { nanoid } from 'nanoid';

import { parse as parseTwemoji } from '@twemoji/parser';

export function capitalizeFirstLetter(string) {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
}

export function slugify(string) {
    return string ? slug(string) : '';
}

export function truncate(str, count = 20, splitType = 'word') {
    return splitType === 'word' ? (str || '').split(' ').splice(0, count).join(' ') : (str || '').slice(0, count);
}

export function copyToClipboard(text) {
    return copy(text);
}

export function textToColor(str) {
    str = `${str}`;
    let hash = 0;
    for(let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for(let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += (`00${value.toString(16)}`).substr(-2);
    }
    return color;
}

export function hex2rgb(hex) {
	if(hex.length !== 7) { // #333 format
		hex = `#${hex.slice(1, 2).repeat(2)}${hex.slice(2, 3).repeat(2)}${hex.slice(-1).repeat(2)}`;
	}
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // return {r, g, b}
    return { r, g, b };
}

// from: https://gist.github.com/kongchen/941a652882d89bb96f87
export function utf8To16(input) {
    const _escape = s => {
        function q(c) {
            const e = c.charCodeAt();
            return `%${(e < 16 ? '0' : '')}${e.toString(16).toUpperCase()}`;
        }
        return s.replace(/[\x00-),:-?[-^`{-\xFF]/g, q);
    };
    try {
        return decodeURIComponent(_escape(input));
    } catch (URIError) {
        // include invalid character, cannot convert
        return input;
    }
}

export function stripTags(text) {
    return text ? text.replace(/(<([^>]+)>)/ig, '') : text;
}

const HTML_ESCAPES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

// Neutralise le balisage d'un texte saisi avant de l'injecter en HTML (titres de
// couverture rendus avec des images emoji, où DOMPurify ne s'applique pas en SSR).
export function escapeHtml(text) {
    return `${text || ''}`.replace(/[&<>"']/g, char => HTML_ESCAPES[char]);
}

export function nl2br(str, isXhtml) {
    if(typeof str === 'undefined' || str === null) {
        return '';
    }
    const breakTag = (isXhtml || typeof isXhtml === 'undefined') ? '<br />' : '<br>';
    return `${str}`.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, `$1${breakTag}$2`);
}

export function splitCamelCase(s) {
   return `${s}`.split(/([A-Z][a-z]+)/).filter(e => e);
}

export function removeEmojis(text) {
    // Retire uniquement les emojis (Twemoji), sans toucher aux écritures
    // non latines : hangul, kana, kanji, cyrillique, etc. L'ancien filtre
    // `[^\x00-\xFF]` vidait les titres de couverture japonais et coréens.
    const str = `${text || ''}`;
    const entities = parseTwemoji(str, { assetType: 'svg' });
    let result = str;
    if(entities.length) {
        result = '';
        let lastIndex = 0;
        for(const entity of entities) {
            result += str.slice(lastIndex, entity.indices[0]);
            lastIndex = entity.indices[1];
        }
        result += str.slice(lastIndex);
    }
    return result
        .replace(/[\uFE0E\uFE0F]/g, '') // sélecteurs de variation emoji
        .replace(/[\u200D]/g, ''); // zero-width joiner (séquences emoji)
}

export function encodeText(text) {
    try {
        return encodeURIComponent(text);
    } catch (e) {
        console.error(e, text);
        return text;
    }
}

export function normalizeEmoji(text) {
    const str = `${text || ''}`;
    const entities = parseTwemoji(str, { assetType: 'svg' });
    if (!entities.length) return str;
    let result = '';
    let lastIndex = 0;
    for (const entity of entities) {
        result += str.slice(lastIndex, entity.indices[0]);
        result += entity.url
            ? `<img class="emoji" draggable="false" alt="${entity.text}" src="${entity.url}">`
            : '';
        lastIndex = entity.indices[1];
    }
    result += str.slice(lastIndex);
    return result;
}

export function getRandomString(size = 10) {
    // polyfills.js
    if(typeof window !== 'undefined' && !window.crypto) {
        window.crypto = window.msCrypto;
    }

    return nanoid(size);
}

export function base64ToBlob(base64, type = 'application/octet-stream') {
	const binStr = atob(base64);
	const len = binStr.length;
	const arr = new Uint8Array(len);
	for(let i = 0; i < len; i += 1) {
		arr[i] = binStr.charCodeAt(i);
	}
	return new Blob([arr], { type });
}

export function escapeRegExp(str) {
	return `${str || ''}`.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}

// Normalise un texte pour la recherche : minuscules + suppression des diacritiques
// (« Café » ↔ « cafe »). Utilisé pour comparer requête et messages sans souci d'accents.
export function normalizeSearchText(str) {
	return `${str || ''}`.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

// ── Largeur visuelle et césure des écritures à chasse pleine (CJK) ───────────
// Idéogrammes, kana et hangul occupent une chasse pleine : environ deux fois la
// largeur d'un caractère latin à taille de police égale. Les bulles du livre sont
// en `white-space: nowrap` (book.css), donc la découpe calculée côté JS est la
// césure définitive du PDF : une ligne mal mesurée déborde de la page.
const FULL_WIDTH_CHAR_REGEXP = /[\u1100-\u115F\u2E80-\u303E\u3041-\u33FF\u3400-\u4DBF\u4E00-\u9FFF\uA000-\uA4CF\uA960-\uA97C\uAC00-\uD7A3\uF900-\uFAFF\uFE10-\uFE19\uFE30-\uFE6F\uFF00-\uFF60\uFFE0-\uFFE6]/;

// Kinsoku shori réduit au plus visible en impression : une ligne ne commence pas par
// une ponctuation fermante ou un petit kana, et ne se termine pas par une ouvrante.
const CJK_NO_LINE_START_CHARS = '、。，．：；！？）］｝」』〉》】〕ー々ぁぃぅぇぉっゃゅょゎァィゥェォッャュョヮ';
const CJK_NO_LINE_END_CHARS = '（［｛「『〈《【〔';

function getCharVisualWidth(char) {
	return FULL_WIDTH_CHAR_REGEXP.test(char) ? 2 : 1;
}

/** Le texte contient-il au moins un caractère à chasse pleine (japonais, coréen, chinois) ? */
export function hasFullWidthChars(text) {
	return FULL_WIDTH_CHAR_REGEXP.test(`${text || ''}`);
}

/**
 * Largeur d'un texte exprimée en « caractères latins ». Vaut exactement `text.length`
 * pour du texte latin, ce qui laisse les budgets de pagination existants inchangés.
 * Les paires de substitution (emojis) comptent 2, comme aujourd'hui.
 */
export function getTextVisualWidth(text) {
	const source = `${text || ''}`;
	let width = 0;
	for(let i = 0; i < source.length; i += 1) {
		width += getCharVisualWidth(source[i]);
	}
	return width;
}

// En dessous de ce taux de remplissage, couper à l'espace laisserait un blanc trop
// visible en fin de ligne : on préfère alors couper entre deux caractères pleine chasse.
const SPACE_BREAK_MIN_FILL = 0.6;

function measureWidth(source, start, end) {
	let width = 0;
	for(let i = start; i < end; i += 1) {
		width += getCharVisualWidth(source[i]);
	}
	return width;
}

/**
 * Découpe un texte en lignes dont la largeur visuelle ne dépasse pas `maxWidth`
 * (exprimé en caractères latins).
 *
 * Deux types de coupe sont suivis en parallèle, ce qui couvre les deux écritures
 * sans avoir à détecter la langue : le coréen s'écrit avec des espaces et coupe
 * donc entre les mots (équivalent de word-break: keep-all), tandis que le japonais
 * et le chinois, qui n'en ont quasiment pas, coupent entre deux idéogrammes.
 */
export function wrapTextByVisualWidth(text, maxWidth) {
	const source = `${text || ''}`;
	if(!source || maxWidth <= 0) {
		return [source];
	}

	const lines = [];
	let lineStart = 0;
	let width = 0;
	let spaceBreakAt = -1; // Après un espace : coupe entre deux mots
	let fullWidthBreakAt = -1; // Entre deux caractères pleine chasse

	for(let i = 0; i < source.length; i += 1) {
		const char = source[i];
		const charWidth = getCharVisualWidth(char);

		if(width + charWidth > maxWidth && i > lineStart) {
			const spaceIsUsable = spaceBreakAt > lineStart
				&& measureWidth(source, lineStart, spaceBreakAt) >= maxWidth * SPACE_BREAK_MIN_FILL;
			let cut = i; // Sans coupe exploitable, on tranche net plutôt que de déborder
			if(spaceIsUsable) {
				cut = spaceBreakAt;
			} else if(fullWidthBreakAt > lineStart) {
				cut = fullWidthBreakAt;
			}

			lines.push(source.substring(lineStart, cut));
			lineStart = cut;
			spaceBreakAt = -1;
			fullWidthBreakAt = -1;
			width = measureWidth(source, lineStart, i + 1);
		} else {
			width += charWidth;
		}

		if(char === ' ') {
			spaceBreakAt = i + 1;
		} else if(charWidth === 2
			&& CJK_NO_LINE_END_CHARS.indexOf(char) < 0
			&& (i + 1 >= source.length || CJK_NO_LINE_START_CHARS.indexOf(source[i + 1]) < 0)) {
			fullWidthBreakAt = i + 1;
		}
	}
	lines.push(source.substring(lineStart));

	return lines;
}
